import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Stack,
} from "@mui/material";
import { QuotationResponseDto } from "../../shared/dtos/quotation/QuotationResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

interface QuotationDetailsTabProps {
  quote: QuotationResponseDto;
}

const QuotationDetailsTab: React.FC<QuotationDetailsTabProps> = ({ quote }) => {
  const { header, items, taxBreakdowns, charges } = quote;

  return (
    <Grid container spacing={3}>
      {/* Left Column: Info */}
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Quote To
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography
                variant="body1"
                gutterBottom
                sx={{ fontWeight: "bold" }}
              >
                {quote.customerName}
              </Typography>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Valid Until:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color:
                      new Date(quote.expiryDate) < new Date()
                        ? "error.main"
                        : "text.secondary",
                  }}
                >
                  {new Date(quote.expiryDate).toLocaleDateString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                Company Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {header?.companySnapshot?.companyName}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {header?.companySnapshot?.address}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Tel: {header?.companySnapshot?.phone}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Email: {header?.companySnapshot?.email}
              </Typography>
            </CardContent>
          </Card>

          {quote.termsAndConditions && (
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Terms & Conditions
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", whiteSpace: "pre-wrap" }}
                >
                  {quote.termsAndConditions}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Stack>
      </Grid>

      {/* Right Column: Items & Breakdowns */}
      <Grid size={{ xs: 12, md: 8 }}>
        <Card
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
            bgcolor: "background.paper",
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              Estimated Items
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ bgcolor: "background.default" }}>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Disc/Unit</TableCell>
                    <TableCell align="right">Tax Amt</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items?.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                          {item.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{item.qty}</TableCell>
                      <TableCell align="right">
                        {formatPlainNumber(item.price)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "error.main" }}>
                        {item.discount > 0
                          ? formatPlainNumber(item.discount)
                          : "-"}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "text.secondary" }}>
                        {formatPlainNumber(item.taxAmount)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: "bold" }}>
                        {formatPlainNumber(item.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Breakdowns & Summary Grid */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                height: "100%",
                bgcolor: "background.paper",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Tax & Charges Breakdown
                </Typography>

                {/* Charges */}
                {charges && charges.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ color: "text.secondary", fontWeight: "bold" }}
                    >
                      Applied Charges
                    </Typography>
                    {charges.map((charge, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption">
                          {charge.name} ({charge.rate}% - {charge.chargeType})
                        </Typography>
                        <Typography variant="caption">
                          {formatPlainNumber(charge.amount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Tax Breakdown */}
                {taxBreakdowns && taxBreakdowns.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: charges && charges.length > 0 ? 2 : 0,
                      borderTop:
                        charges && charges.length > 0
                          ? "1px dashed rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ color: "text.secondary", fontWeight: "bold" }}
                    >
                      Tax Breakdown
                    </Typography>
                    {taxBreakdowns.map((tax) => (
                      <Box
                        key={tax.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="caption">{tax.name}</Typography>
                        <Typography variant="caption">
                          {formatPlainNumber(tax.taxAmount)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}

                {(!charges || !charges.length) &&
                  (!taxBreakdowns || !taxBreakdowns.length) && (
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      No additional charges or taxes applied.
                    </Typography>
                  )}
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <Card
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
                height: "100%",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: "bold" }}
                >
                  Summary
                </Typography>

                {/* Currency and Exchange Rate */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                    mt: 2,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Currency
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold", color: "secondary.main" }}
                  >
                    {quote.currencyCode || "KES"}
                  </Typography>
                </Box>

                {Number(quote.exchangeRate) !== 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      Exchange Rate
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "bold", color: "secondary.main" }}
                    >
                      {quote.exchangeRate}
                    </Typography>
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Subtotal
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatPlainNumber(quote.subtotal)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Total Discount
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: "error.main" }}
                  >
                    -{formatPlainNumber(quote.totalDiscount)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Total Tax
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatPlainNumber(quote.totalTax)}
                  </Typography>
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Grand Total */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    Estimated Total
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {quote.currencyCode} {formatPlainNumber(quote.total)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default QuotationDetailsTab;
